<template>
    <ModalWrapper v-model="dialog" class="choose-agent-dialog" theme="light" :max-width="newAgentMode ? 800 : 600">
        <template v-slot:activator="{props}">
            <slot name="activator">
                <div
                    v-ripple
                    class="text-right py-1 text-subtitle-2 cursor-pointer d-flex align-center"
                    style="background-color: rgba(0, 0, 0, 0.6); color: rgba(255, 255, 255, 0.6)"
                    :class="{'px-10': !isMobile, 'px-4': isMobile, 'py-2': isMobile, 'justify-end': isButtonRight}"
                    v-bind="props"
                    v-text="`${$t('choose.choose')} ${$t('choose.agent_client')}`"
                />
            </slot>
        </template>
        <template v-slot:title>
            <div class="d-flex align-center">
                <v-btn
                    v-if="newAgentMode"
                    variant="text"
                    class="me-1"
                    size="small"
                    color="grey-darken-2"
                    elevation="0"
                    @click="newAgentMode = false"
                >
                    <v-icon> mdi-arrow-left</v-icon>
                </v-btn>
                <span :class="{'text-h6': isMobile}">
                    {{ newAgentMode ? `${$t('create_new_agent')}` : `${$t('choose.agent_dialog_header')}` }}
                </span>
            </div>
        </template>
        <template v-slot:content>
            <div v-show="!newAgentMode">
                <client-type-chooser v-model="clientType" />
                <v-form ref="form" v-model="valid">
                    <private-client
                        v-if="clientType === 'pc'"
                        v-model="selectedClient"
                        :_new-client="newClient"
                        :_is-new-client-mode="isNewClientMode"
                        @change-new-client="newClient = $event"
                        @change-is-new-client-mode="isNewClientMode = $event"
                    />
                    <corporate-client v-if="clientType === 'cc'" v-model="selectedCompany" hide-new-client />
                    <tour-agency
                        v-if="clientType === 'ta'"
                        ref="tourAgency"
                        :_selected-client="selectedAgent"
                        :_new-agent-mode="newAgentMode"
                        :_selected-company="selectedCompany"
                        hide-new-client
                        @change-selected-client="selectedAgent = $event"
                        @change-new-agent-mode="newAgentMode = $event"
                        @change-selected-company="selectedCompany = $event"
                    />
                </v-form>
            </div>
            <person-form
                v-if="selectedCompany && newAgentMode"
                class="px-8 py-8"
                :person-data="{}"
                :role-prefix="selectedCompany.type === 'TOUR_AGENCY' ? 'ta' : 'to'"
                :company-id="selectedCompany.id"
                :custom-roles="[
                    'accountant',
                    'agent',
                    'director',
                    'guest',
                    'manager',
                    'sales_manager',
                    'sales_staff',
                    'supervisor',
                ]"
                allow-no-credentials
                :departments="departments"
                @save="onSavePersonForm"
                @close-popup="newAgentMode = false"
            />
        </template>
        <template v-slot:btns>
            <v-btn v-show="!newAgentMode" color="primary" :disabled="!valid" @click="save">
                {{ $t('continue') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>

<script lang="ts">
    import {Component, Emit, Setup, Vue, Watch} from 'vue-facing-decorator'
    import PersonForm from '~src/components/account/forms/personForm.src'
    import ClientTypeChooser from '~src/components/account/snippets/ClientTypeChooser.vue'
    import PrivateClient from '~src/components/account/modals/newOrderRequest/forms/PrivateClient.vue'
    import CorporateClient from '@/components/account/modals/newOrderRequest/forms/CorporateClient.vue'
    import TourAgency from '~src/components/account/modals/newOrderRequest/forms/TourAgency.vue'
    import {authorizationPersonRsTransformer} from '~/stores/AuthStore'

    @Component({
        components: {TourAgency, CorporateClient, PrivateClient, ClientTypeChooser, PersonForm},
    })
    export class ChooseAgentDialog extends Vue {
        @Setup(() => useLocalized()) localized: Function

        dialog = false
        clientType = null
        valid = false
        newAgentMode = false
        isNewClientMode = false

        selectedCompany = null
        selectedAgent = null
        selectedClient = null
        departments = []

        newClient = {
            prefix: null,
            firstName: null,
            lastName: null,
            contactPhone: null,
            email: null,
        }

        @Watch('clientType')
        onClientTypeChange() {
            this.reset()
            if (this.clientType === 'ta') {
                this.loadTAData()
            }
        }

        async loadTAData() {
            await runtimeStore.loadAvailablePersonRoles('TOUR_AGENCY')
            if (authStore.isTO1 || authStore.isTO2) await this.loadDepartments()
        }

        @Watch('dialog')
        async onFormChange() {
            if (this.newAgentMode) this.newAgentMode = false
        }

        async loadDepartments() {
            try {
                const {departments} = await this.$api.departments.get()
                this.departments = departments.map(d => ({
                    ...d,
                    nameOriginal: d.name,
                    name: this.localized(d.name),
                }))
            } catch (e) {
                this.departments = []
            }
        }

        reset() {
            this.selectedCompany = null
            this.selectedAgent = null
            this.selectedClient = null

            this.isNewClientMode = false
            this.newClient = {
                firstName: null,
                lastName: null,
                contactPhone: null,
                email: null,
            }
        }

        @Emit()
        async save() {
            if (this.isNewClientMode) {
                const rs = await this.$api.registration.post({
                    ...this.newClient,
                    directSales: true,
                    companyId: this.selectedCompany?.id,
                })
                this.selectedClient = authorizationPersonRsTransformer(rs)
            }

            if (this.selectedClient) {
                persistentStore.SET_SELECTED_PRIVATE_CLIENT({
                    ...this.selectedClient,
                })
            } else if (this.selectedCompany) {
                persistentStore.SET_SELECTED_COMPANY_AND_AGENT({
                    company: {
                        ...this.selectedCompany,
                    },
                    takeClientMethod: this.selectedCompanyIsCorporate ? 'persons' : 'privateClients',
                })

                if (this.selectedAgent) {
                    persistentStore.SET_SELECTED_COMPANY_AND_AGENT({
                        ...persistentStore.selectedCompanyAndAgent,
                        agent: {
                            ...this.selectedAgent,
                        },
                        takeClientMethod: 'privateClients',
                    })
                }
            }

            this.dialog = false
        }

        onSavePersonForm(newUser) {
            if (newUser) {
                this.selectedAgent = newUser
                this.newAgentMode = false
            }
        }

        get selectedCompanyIsCorporate() {
            return this.selectedCompany?.type === 'CORPORATOR'
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get isButtonRight() {
            return false
        }
    }

    export default ChooseAgentDialog
</script>
